import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import HomePage from './pages/HomePage';
import ArticlePage from './pages/ArticlePage';
import TrustlyPage from './pages/TrustlyPage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import SEO from './components/SEO';
import FloatingButtons from './components/FloatingButtons';
import { Menu, X } from "lucide-react";

function App() {
  const [menuOpen, setMenuOpen] = React.useState(false);

  const navLinks = [
    { to: "/", label: "Hjem" },
    { to: "/hurtig-udbetaling", label: "Hurtige Udbetalinger" },
    { to: "/trustly", label: "Trustly" },
    // { to: "/bonusser", label: "Bonusser" },
    { to: "/om-os", label: "Om os" },
    // { to: "/privatlivspolitik", label: "Privatlivspolitik" },
    { to: "/kontakt", label: "Kontakt" },
  ];



  const closeMenu = () => setMenuOpen(false);

  return (
    <HelmetProvider>


      <Router>
        {/* JSON-LD using Helmet */}
        <SEO />
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
          {/* Header */}
          <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-blue-800/50 shadow-lg shadow-blue-900/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-4">
                 {/* Logo + Site Name */}
                                <Link to="/" className="group flex items-center gap-3" onClick={closeMenu}>
                                  <div className="relative">
                                    <img
                                      src="/logocircle3.png"
                                      alt="CasinoGuide.dk – Danmarks bedste guide til udenlandske casinoer 2025"
                                      width={80}
                                      height={80}
                                      className="rounded-full shadow-2xl object-cover border-2 border-cyan-500/50"
                                      loading="eager"
                                    />
                                    <span className="absolute -bottom-1 -right-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-xs font-black px-2 py-0.5 rounded-full shadow-lg">
                                      JSNK
                                    </span>
                                  </div>
                
                                  <div>
                                    <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 bg-clip-text text-transparent">
                                      CasinoGuide.dk
                                    </h1>
                                    <p className="text-xs text-cyan-300 -mt-1 opacity-90">Casinoer med Hurtige udbetalinger</p>
                                  </div>
                                </Link>

                {/* DESKTOP NAV */}


                <nav className="hidden md:flex gap-8 text-sm font-medium" aria-label="Hovednavigation">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="text-blue-200 hover:text-cyan-400 transition pb-1 border-b-2 border-transparent hover:border-cyan-400"
                      aria-label={`Gå til ${link.label}`}
                      onClick={closeMenu}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>


                {/* MOBILE BURGER BUTTON */}
                <button
                  className="md:hidden text-blue-200 hover:text-cyan-400 transition"
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-label={menuOpen ? "Luk menu" : "Åbn menu"}
                  aria-expanded={menuOpen}
                >
                  {menuOpen ? <X size={30} /> : <Menu size={30} />}
                </button>
              </div>
            </div>

            {/* MOBILE DROPDOWN MENU */}
            <div
              className={`md:hidden bg-slate-900/95 backdrop-blur-sm border-t border-blue-800/40 transition-all overflow-hidden ${menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              aria-hidden={!menuOpen}
            >
              <nav className="flex flex-col px-6 py-4 text-blue-200 text-lg space-y-4" aria-label="Mobilnavigation">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={closeMenu}
                    className="hover:text-cyan-400 transition"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-grow" role="main">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/hurtig-udbetaling" element={<ArticlePage />} />
              <Route path="/trustly" element={<TrustlyPage />} />
              <Route path="/om-os" element={<AboutPage />} />
              <Route path="/privatlivspolitik" element={<PrivacyPage />} />
              <Route path="/kontakt" element={<ContactPage />} />
            </Routes>
          </main>

          {/* Footer */}
          <footer className="bg-slate-900/90 border-t border-blue-800/50 mt-16" role="contentinfo">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="text-blue-300 text-sm space-y-1">
                  <p className="flex items-center gap-2">
                    <span className="text-cyan-400">18+</span> |
                    <span>Spil ansvarligt</span> |
                    <a
                      href="https://www.rofus.nu"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-cyan-400 underline underline-offset-2 transition-colors"
                      aria-label="Selvudelukkelse via ROFUS (åbner i nyt vindue)"
                    >
                      Selvudelukkelse via ROFUS.nu
                    </a>
                  </p>
                  <p className="text-blue-400">
                    Kontakt Spillemyndighedens hjælpelinje på{' '}
                    <a
                      href="https://www.stopspillet.dk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-cyan-400 underline underline-offset-2 transition-colors"
                      aria-label="StopSpillet.dk - Spillemyndighedens hjælpelinje (åbner i nyt vindue)"
                    >
                      StopSpillet.dk
                    </a>
                  </p>
                </div>
                <div className="flex gap-6 md:justify-end items-center">
                  <Link
                    to="/om-os"
                    className="text-blue-300 hover:text-cyan-400 text-sm transition-colors duration-300 hover:underline underline-offset-4"
                  >
                    Om os
                  </Link>
                  <Link
                    to="/privatlivspolitik"
                    className="text-blue-300 hover:text-cyan-400 text-sm transition-colors duration-300 hover:underline underline-offset-4"
                  >
                    Privatlivspolitik
                  </Link>
                  <Link
                    to="/kontakt"
                    className="text-blue-300 hover:text-cyan-400 text-sm transition-colors duration-300 hover:underline underline-offset-4"
                  >
                    Kontakt
                  </Link>
                </div>
              </div>
              <div className="pt-6 border-t border-blue-800/50 text-center text-blue-400 text-xs">
                © 2025 CasinoGuide.dk - Alle rettigheder forbeholdes
              </div>
            </div>
          </footer>

          <FloatingButtons />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;